// MeetingComposer — schedule (or start) a meeting from a Circle. Providers come
// from tenant discovery (0 → disabled with guidance, 1 → auto-selected, 2+ →
// selector). Circle members are the default attendees (resolved + invited by the
// backend; members without an email are skipped). Posts a MEETING card into the
// thread. Timezone follows the organizer's IANA zone (never blind UTC).
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Video, X, Loader2, CalendarClock } from 'lucide-react';
import type { MeetingProviders } from '../types';

interface Props {
  circleName: string;
  memberCount: number;
  providers?: MeetingProviders;
  loadingProviders: boolean;
  /** true = "Meet now" (start immediately + always generate a link). */
  meetNow?: boolean;
  canConfigureIntegrations: boolean;
  onCreate: (input: {
    title: string;
    startAt: string;
    endAt?: string;
    description?: string;
    timezone: string;
    provider?: string;
    generateLink: boolean;
  }) => Promise<void>;
  onClose: () => void;
}

// Match the calendar's New Meeting dialog so durations are consistent app-wide
// (minutes and hours, not minutes only).
const DURATIONS: { value: number; label: string }[] = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
];

// The organizer's IANA zone — the wall clock they typed is interpreted here, then
// sent with its offset so leave-service stores the correct UTC instant.
function localZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export const MeetingComposer: React.FC<Props> = ({
  circleName,
  memberCount,
  providers,
  loadingProviders,
  meetNow,
  canConfigureIntegrations,
  onCreate,
  onClose,
}) => {
  const { t } = useTranslation('conversations');
  const zone = useMemo(localZone, []);
  const [title, setTitle] = useState(`${circleName} ${t('meeting.suffix', { defaultValue: 'Meeting' })}`);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState<number>(30);
  const [description, setDescription] = useState('');
  const [provider, setProvider] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const usableAll = useMemo(() => (providers?.providers ?? []).filter((p) => p.can_create_link), [providers]);
  // "Meet now" needs the instant-meeting capability specifically (not every provider
  // config supports starting immediately) — derive from capabilities, not provider name.
  const usable = useMemo(
    () => (meetNow ? usableAll.filter((p) => p.capabilities.includes('createInstantMeeting')) : usableAll),
    [usableAll, meetNow],
  );
  // 1 provider → auto-select; else preselect the tenant default when usable.
  useEffect(() => {
    if (usable.length === 1) setProvider(usable[0].provider);
    else if (providers?.default && usable.some((p) => p.provider === providers.default)) setProvider(providers.default);
  }, [usable, providers?.default]);

  const noProvider = !loadingProviders && usable.length === 0;
  // Providers exist but none can start instantly → guide the user to schedule.
  const noInstant = !loadingProviders && meetNow && usable.length === 0 && usableAll.length > 0;

  const submit = async () => {
    const name = title.trim();
    if (!name || busy || noProvider) return;
    let startAt: string;
    let endAt: string | undefined;
    if (meetNow) {
      const now = new Date();
      startAt = now.toISOString();
      endAt = new Date(now.getTime() + duration * 60000).toISOString();
    } else {
      if (!date || !time) {
        setErr(t('meeting.need_time', { defaultValue: 'Pick a date and time.' }));
        return;
      }
      // Interpret the typed wall clock in the organizer's local zone (the browser
      // zone == their zone); toISOString() carries the correct offset.
      const start = new Date(`${date}T${time}`);
      if (Number.isNaN(start.getTime())) {
        setErr(t('meeting.need_time', { defaultValue: 'Pick a date and time.' }));
        return;
      }
      startAt = start.toISOString();
      endAt = new Date(start.getTime() + duration * 60000).toISOString();
    }
    setBusy(true);
    setErr('');
    try {
      await onCreate({
        title: name,
        startAt,
        endAt,
        description: description.trim() || undefined,
        timezone: zone,
        provider: provider || undefined,
        generateLink: true,
      });
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('meeting.failed', { defaultValue: 'Could not schedule the meeting.' }));
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 w-full rounded-xl border border-border bg-card p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-charcoal">
          {meetNow ? <Video className="h-4 w-4" /> : <CalendarClock className="h-4 w-4" />}
          {meetNow ? t('meeting.meet_now', { defaultValue: 'Meet now' }) : t('meeting.schedule', { defaultValue: 'Schedule meeting' })}
        </h4>
        <button type="button" onClick={onClose} className="text-muted-foreground hover:text-charcoal" aria-label={t('edit.cancel', { defaultValue: 'Cancel' })}>
          <X className="h-4 w-4" />
        </button>
      </div>

      {noInstant ? (
        <div className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">
          {t('meeting.no_instant', { defaultValue: "Your provider can't start a meeting instantly — schedule one instead." })}
        </div>
      ) : noProvider ? (
        <div className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">
          {t('meeting.no_provider', { defaultValue: 'No meeting provider is configured for this workspace.' })}
          <div className="mt-1">
            {canConfigureIntegrations
              ? t('meeting.configure_hint', { defaultValue: 'Configure a provider in Settings → Integrations.' })
              : t('meeting.ask_admin', { defaultValue: 'Ask your workspace administrator to enable a meeting provider.' })}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <input
            className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm text-charcoal"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('meeting.title_ph', { defaultValue: 'Meeting title' })}
            aria-label={t('meeting.title_ph', { defaultValue: 'Meeting title' })}
          />

          {!meetNow && (
            <div className="grid grid-cols-1 gap-2">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">{t('meeting.date', { defaultValue: 'Date' })}</label>
                <input type="date" className="w-full min-w-0 rounded-lg border border-border bg-background px-2 py-1.5 text-sm text-charcoal" value={date} onChange={(e) => setDate(e.target.value)} aria-label={t('meeting.date', { defaultValue: 'Date' })} />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">{t('meeting.time', { defaultValue: 'Start time' })}</label>
                <input type="time" className="w-full min-w-0 rounded-lg border border-border bg-background px-2 py-1.5 text-sm text-charcoal" value={time} onChange={(e) => setTime(e.target.value)} aria-label={t('meeting.time', { defaultValue: 'Start time' })} />
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs text-muted-foreground">{t('meeting.duration', { defaultValue: 'Duration' })}</label>
            <select className="w-full min-w-0 rounded-lg border border-border bg-background px-2 py-1.5 text-sm text-charcoal" value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
              {DURATIONS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>

          {/* Provider selector only when 2+ usable providers. */}
          {usable.length > 1 && (
            <select className="w-full min-w-0 rounded-lg border border-border bg-background px-2 py-1.5 text-sm text-charcoal" value={provider} onChange={(e) => setProvider(e.target.value)} aria-label={t('meeting.provider', { defaultValue: 'Provider' })}>
              {usable.map((p) => (
                <option key={p.provider} value={p.provider}>{p.label}</option>
              ))}
            </select>
          )}
          {usable.length === 1 && (
            <p className="text-xs text-muted-foreground">{t('meeting.via', { defaultValue: 'Via' })} {usable[0].label}</p>
          )}

          <textarea
            className="w-full resize-none rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm text-charcoal"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('meeting.agenda_ph', { defaultValue: 'Agenda (optional)' })}
          />

          <p className="text-xs text-muted-foreground">
            {t('meeting.attendees_note', { count: memberCount, defaultValue: '{{count}} Circle members will be invited.' })}
          </p>

          {err && <p className="text-xs text-danger">{err}</p>}

          <button
            type="button"
            onClick={submit}
            disabled={busy || !title.trim()}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {meetNow ? t('meeting.start_cta', { defaultValue: 'Start meeting' }) : t('meeting.schedule_cta', { defaultValue: 'Schedule' })}
          </button>
        </div>
      )}
    </div>
  );
};
